import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Truck, ClipboardPaste, Sparkles, CheckCircle2, AlertTriangle,
  ArrowRight, ArrowLeft, ChevronDown, ChevronRight, History, RotateCcw,
  Package, Ban,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Batch Arrivals — Iter G
 * Paste newly-acquired sticker codes → preview affected orders → confirm →
 * atomic reduction of pending across every affected order. Ready remains
 * DERIVED. No inventory management.
 */
export default function BatchArrivals() {
  const [step, setStep] = useState("compose");   // compose | preview | done
  const [raw, setRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/batches", { params: { limit: 25 } });
      setHistory(r.data.batches || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const goPreview = async () => {
    if (!raw.trim()) return toast.error("Paste sticker codes first.");
    setBusy(true);
    try {
      const r = await api.post("/admin/batches/preview", { raw_text: raw });
      setPreview(r.data);
      setStep("preview");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Preview failed.");
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/batches/apply", { raw_text: raw, note });
      setApplied(r.data);
      setStep("done");
      toast.success(`Applied — ${r.data.totals.total_matched} stickers matched, ${r.data.totals.orders_moved_to_ready} orders moved to Ready.`);
      await loadHistory();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Apply failed.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setRaw(""); setNote(""); setPreview(null); setApplied(null); setStep("compose");
  };

  return (
    <div className="space-y-6" data-testid="batch-arrivals">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="kicker kicker-red">Batch arrivals</div>
          <h2 className="font-display text-3xl">Just got a new box of stickers?</h2>
          <p className="text-sm text-[var(--smoke)] mt-1">
            Paste what arrived — we&apos;ll match it against every waiting list and move fulfilled stickers to Ready.
          </p>
        </div>
        <button onClick={() => setShowHistory((v) => !v)} className="btn-ghost text-sm" data-testid="batch-history-toggle">
          <History size={14} /> History ({history.length})
        </button>
      </div>

      {step === "compose" && (
        <ComposeStep raw={raw} setRaw={setRaw} onPreview={goPreview} busy={busy} />
      )}
      {step === "preview" && preview && (
        <PreviewStep
          preview={preview} note={note} setNote={setNote}
          onBack={() => setStep("compose")} onApply={doApply} busy={busy}
        />
      )}
      {step === "done" && applied && (
        <DoneStep applied={applied} onReset={reset} />
      )}

      {showHistory && <HistoryPanel history={history} onRefresh={loadHistory} />}
    </div>
  );
}

function ComposeStep({ raw, setRaw, onPreview, busy }) {
  return (
    <div className="space-y-4" data-testid="batch-compose">
      <textarea
        rows={10}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        className="w-full border-2 border-[var(--ink)] rounded-2xl p-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
        placeholder={`Paste the codes you just received.\n\n1, 5, 12, 42\nECU12, ECU13\n\nWe match these against every waiting list, not against inventory. You'll see the impact before anything is applied.`}
        data-testid="batch-textarea"
        maxLength={100000}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-[var(--smoke)]">Commas, newlines, or spaces all work.</span>
        <button onClick={onPreview} disabled={busy || !raw.trim()} className="btn-red" data-testid="batch-preview-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ClipboardPaste size={14} />} Preview impact
        </button>
      </div>
    </div>
  );
}

function PreviewStep({ preview, note, setNote, onBack, onApply, busy }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (code) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });
  const t = preview.totals || {};
  return (
    <div className="space-y-4" data-testid="batch-preview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Metric label="Incoming" value={t.total_incoming} />
        <Metric label="Will match" value={t.total_matched} tone="green" />
        <Metric label="Unique codes" value={t.unique_codes_incoming} />
        <Metric label="Orders affected" value={t.orders_affected} tone="yellow" />
        <Metric label="Orders → Ready" value={t.orders_will_complete} tone="green" />
      </div>

      {preview.unknowns.length > 0 && (
        <div className="p-3 border-2 border-yellow-700 bg-yellow-50 rounded-lg text-xs" data-testid="batch-unknowns">
          <AlertTriangle size={12} className="inline" /> {preview.unknowns.length} unknown tokens skipped
        </div>
      )}

      {preview.orders_will_complete.length > 0 && (
        <div className="p-4 border-2 border-emerald-900 bg-emerald-50 rounded-2xl" data-testid="batch-ready-list">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900 flex items-center gap-1">
            <Sparkles size={12} /> These orders will move to Ready
          </div>
          <ul className="mt-2 text-sm space-y-1">
            {preview.orders_will_complete.map((o) => (
              <li key={o.public_id} className="flex flex-wrap items-center justify-between gap-2 border border-emerald-900/40 bg-white rounded px-2 py-1" data-testid={`batch-ready-row-${o.public_id}`}>
                <span className="font-mono font-bold">{o.public_id}</span>
                <span className="opacity-70">{o.customer_name}</span>
                <span className="text-[10px] uppercase tracking-widest opacity-70">{o.delivery_code.replaceAll("_", " ")}</span>
                {o.will_auto_transition_to_ready && (
                  <span className="text-[10px] uppercase font-bold text-emerald-900">Will send Ready email</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2 text-left">Sticker</th>
              <th className="px-3 py-2 text-right">Incoming</th>
              <th className="px-3 py-2 text-right">Matching</th>
              <th className="px-3 py-2 text-right">Leftover</th>
            </tr>
          </thead>
          <tbody>
            {preview.matches.map((m) => {
              const open = expanded.has(m.code);
              return (
                <React.Fragment key={m.code}>
                  <tr className="border-b border-[var(--stone)] hover:bg-[var(--panini-yellow-soft)] cursor-pointer" onClick={() => toggle(m.code)} data-testid={`batch-match-row-${m.code}`}>
                    <td className="px-3 py-2">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td className="px-3 py-2 font-mono font-bold">{m.code}</td>
                    <td className="px-3 py-2 text-right">{m.qty_incoming}</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-900">{m.qty_matched}</td>
                    <td className="px-3 py-2 text-right">{m.leftover}</td>
                  </tr>
                  {open && (
                    <tr className="border-b border-[var(--stone)] bg-[var(--paper)]/60">
                      <td></td>
                      <td colSpan={4} className="px-3 py-2">
                        {m.orders.length === 0 ? <em className="opacity-60 text-xs">Nobody needs this code right now.</em> : (
                          <ul className="text-xs space-y-1">
                            {m.orders.map((o) => (
                              <li key={o.public_id} className="flex items-center justify-between border border-[var(--stone)] rounded px-2 py-1 bg-white" data-testid={`batch-alloc-${m.code}-${o.public_id}`}>
                                <span className="font-mono font-bold">{o.public_id}</span>
                                <span className="opacity-70">{o.customer_name}</span>
                                <span className={`text-[10px] uppercase font-bold ${o.priority === "vip" || o.priority === "rush" ? "text-red-800" : "opacity-60"}`}>{o.priority.replaceAll("_", " ")}</span>
                                <span>needs {o.qty_waiting}</span>
                                <span className="font-bold text-emerald-900">→ take {o.qty_matched}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <label className="block" data-testid="batch-note">
        <span className="text-[10px] uppercase tracking-widest font-bold">Note (optional)</span>
        <input
          type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
          placeholder="e.g. Box #4 from supplier · picked up 2/27"
          className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
          data-testid="batch-note-input"
        />
      </label>

      <div className="flex items-center justify-between pt-2">
        <button onClick={onBack} className="btn-ghost text-sm" data-testid="batch-back"><ArrowLeft size={14} /> Back</button>
        <button onClick={onApply} disabled={busy || t.total_matched === 0} className="btn-red" data-testid="batch-confirm">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Confirm &amp; apply
        </button>
      </div>
    </div>
  );
}

function DoneStep({ applied, onReset }) {
  const nav = useNavigate();
  const t = applied.totals || {};
  return (
    <div className="space-y-4" data-testid="batch-done">
      <div className="p-6 border-2 border-emerald-900 bg-emerald-50 rounded-2xl">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-emerald-900" size={28} />
          <div>
            <h3 className="font-display text-2xl text-emerald-900">Batch applied.</h3>
            <p className="text-sm text-emerald-900/80 mt-1">{t.total_matched} stickers moved from Waiting to Ready across {t.orders_affected} order{t.orders_affected === 1 ? "" : "s"}.</p>
          </div>
        </div>
      </div>

      {applied.orders_moved_to_ready.length > 0 && (
        <div className="p-4 border-2 border-[var(--ink)] rounded-2xl bg-white">
          <div className="text-[10px] uppercase tracking-widest font-bold">Auto-transitioned to Ready</div>
          <ul className="mt-2 space-y-1 text-sm">
            {applied.orders_moved_to_ready.map((o) => (
              <li key={o.public_id} className="flex items-center justify-between gap-2 border border-[var(--stone)] rounded px-2 py-1" data-testid={`batch-done-ready-${o.public_id}`}>
                <button onClick={() => nav(`/admin/fulfillment/${o.public_id}`)} className="font-mono font-bold text-left hover:underline">{o.public_id}</button>
                <span className="text-[10px] uppercase tracking-widest opacity-60">Was {o.prev_status.replaceAll("_", " ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Object.keys(applied.leftover_by_code || {}).length > 0 && (
        <div className="p-4 border-2 border-yellow-700 bg-yellow-50 rounded-2xl">
          <div className="text-[10px] uppercase tracking-widest font-bold text-yellow-900 flex items-center gap-1">
            <Package size={12} /> Leftover — no one is waiting for these
          </div>
          <div className="mt-2 flex flex-wrap gap-1 text-xs">
            {Object.entries(applied.leftover_by_code).map(([c, q]) => (
              <span key={c} className="border border-yellow-700 bg-white rounded px-2 py-0.5 font-mono">{c} × {q}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button onClick={onReset} className="btn-ghost text-sm" data-testid="batch-reset"><RotateCcw size={14} /> Process another batch</button>
      </div>
    </div>
  );
}

function HistoryPanel({ history, onRefresh }) {
  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white" data-testid="batch-history-panel">
      <div className="flex items-center justify-between p-3 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
        <div className="font-display text-lg flex items-center gap-2"><History size={16} /> Batch history</div>
        <button onClick={onRefresh} className="btn-ghost text-xs" data-testid="batch-history-refresh"><RotateCcw size={12} /> Refresh</button>
      </div>
      <div className="max-h-[400px] overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest border-b border-[var(--stone)]">
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-right">Stickers</th>
              <th className="px-3 py-2 text-right">Orders</th>
              <th className="px-3 py-2 text-right">→ Ready</th>
              <th className="px-3 py-2 text-left">Note</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center opacity-60">No batches yet.</td></tr>
            )}
            {history.map((b) => (
              <tr key={b.id} className="border-b border-[var(--stone)]" data-testid={`batch-history-row-${b.id.slice(0, 8)}`}>
                <td className="px-3 py-2 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{b.created_by}</td>
                <td className="px-3 py-2 text-right font-mono">{b.totals?.total_matched ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono">{b.totals?.orders_affected ?? 0}</td>
                <td className="px-3 py-2 text-right font-mono">{b.totals?.orders_moved_to_ready ?? 0}</td>
                <td className="px-3 py-2 text-xs italic opacity-70">{b.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const cls = {
    green: "bg-emerald-100 border-emerald-900 text-emerald-900",
    yellow: "bg-yellow-100 border-yellow-900 text-yellow-900",
  }[tone] || "bg-white border-[var(--ink)] text-[var(--ink)]";
  return (
    <div className={`p-3 rounded-lg border-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{label}</div>
      <div className="mt-1 font-display text-2xl">{value ?? 0}</div>
    </div>
  );
}
